import type React from 'react';

import { Alert, Button, Modal, ModalBody, ModalFooter, ModalHeader } from '@change/ds-core-ui';

import { Translate, useI18n } from '@change-corgi/core-react/i18n';

import type { PartialComment } from '../hooks/types';
import { useDeleteComment } from '../hooks/useDeleteComment/useDeleteComment';

const CommentDeleteErrorMessage: React.FC = () => {
	return (
		<Alert variant="error" className="my-4 p-2">
			<Translate value="fe.errors.generic_try_again" />
		</Alert>
	);
};

export type CommentDeleteModalProps = {
	comment: PartialComment;
	onDeleted: () => void;
	trigger: React.ReactNode;
};

export const CommentDeleteModal: React.FC<CommentDeleteModalProps> = ({ comment, onDeleted, trigger }) => {
	const { translate } = useI18n();
	const {
		data: { error, isPending },
		actions: { deleteComment },
	} = useDeleteComment(comment);

	const handleConfirmDelete = async (close: () => void) => {
		const success = await deleteComment();
		if (success) {
			close();
			onDeleted();
		}
	};

	return (
		<Modal trigger={trigger} isDismissable size="small">
			<ModalHeader
				title={translate('fe.components.comment.delete_comment_modal.title')}
				subtitle={translate('fe.components.comment.delete_comment_modal.subtitle')}
			/>
			<ModalBody>{error && <CommentDeleteErrorMessage />}</ModalBody>
			<ModalFooter>
				{({ close }) => (
					<div className="mt-6 flex h-full w-full items-end justify-end gap-4 p-2">
						<Button
							variant="secondary"
							size="small"
							onClick={close}
							data-qa="cancel-delete-button"
							data-testid="cancel-delete-button"
							disabled={isPending}
						>
							<Translate value="fe.components.confirm_modal.buttons.cancel" />
						</Button>
						<Button
							size="small"
							onClick={async () => handleConfirmDelete(close)}
							data-qa="confirm-delete-button"
							data-testid="confirm-delete-button"
							variant="primary"
							disabled={isPending}
						>
							<Translate value="fe.components.confirm_modal.buttons.delete" />
						</Button>
					</div>
				)}
			</ModalFooter>
		</Modal>
	);
};
